'use client';

import { c as _c } from "react/compiler-runtime";
import type { InboxPullRequestSnoozesResponse } from '@/app/(home)/inbox/api/pull-requests/snoozes/route';
import type { InboxPullRequest } from '@/app/(home)/inbox/types';
import type { InboxSnoozes } from '@/app/(home)/inbox/utils/snooze';
import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useEventCallback } from 'usehooks-ts';
import { describeSnoozeTime, getActiveSnoozes, getPrSnoozeKey, getSnoozedUntil, getSnoozeKey } from '@/app/(home)/inbox/utils/snooze';
import { fetchInboxV2 } from '@/data/pull-requests/fetch-inbox-v2';
import { INBOX_V2_SIDE_LOAD_QUERY_OPTIONS } from '@/data/pull-requests/inbox-v2-side-load';
import { inboxV2SnoozesQueryKey } from '@/data/pull-requests/query-keys';
import { useNow } from '@/hooks/use-now';
const SNOOZES_PATH = '/inbox/api/pull-requests/snoozes';
const SNOOZE_WRITE_KEY = [...inboxV2SnoozesQueryKey(), 'write'] as const;
const NO_SNOOZES: InboxSnoozes = {};

/**
 * The viewer's snoozes that have not ended, checked again every minute so a snooze ends on its
 * own, with the time of that check. It fails open: while the snoozes can't load, nothing is
 * snoozed.
 */
export function useInboxV2Snoozes(t0) {
  const $ = _c(12);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const now = useNow();
  let t2;
  if ($[2] !== now) {
    t2 = snoozes => getActiveSnoozes(snoozes, now);
    $[2] = now;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectActive = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = inboxV2SnoozesQueryKey();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = options.enabled ?? true;
  let t5;
  if ($[5] !== selectActive || $[6] !== t4) {
    t5 = {
      ...INBOX_V2_SIDE_LOAD_QUERY_OPTIONS,
      queryKey: t3,
      queryFn: _temp2,
      select: selectActive,
      structuralSharing: true,
      enabled: t4
    };
    $[5] = selectActive;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const query = useQuery(t5);
  const t6 = query.isError ? NO_SNOOZES : query.data ?? NO_SNOOZES;
  const t7 = query.isLoading && query.failureCount === 0;
  let t8;
  if ($[8] !== now || $[9] !== t6 || $[10] !== t7) {
    t8 = {
      snoozes: t6,
      now,
      isLoading: t7
    };
    $[8] = now;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
}

/** One PR's snooze to write: a time in epoch ms, or none. */
async function _temp2(t0) {
  const {
    signal
  } = t0;
  const data = await fetchInboxV2({
    path: SNOOZES_PATH,
    installationId: null,
    query: {},
    signal,
    description: "snoozes"
  });
  return Object.fromEntries(data.snoozes.map(_temp));
}
function _temp(t0) {
  const {
    owner,
    repo,
    number,
    until
  } = t0;
  return [getSnoozeKey(owner, repo, number), Date.parse(until)];
}
type SnoozeWrite = {
  pr: InboxPullRequest;
  until: number | null;
  after?: Promise<void>;
};
async function writeSnooze({
  pr,
  until,
  after
}: SnoozeWrite) {
  // An Undo waits for the write it reverses, so the server applies the two in order.
  await after;
  const params = {
    owner: pr.repository.owner,
    repo: pr.repository.name,
    number: `${pr.number}`
  };
  const response = until === null ? await fetch(`${SNOOZES_PATH}?${new URLSearchParams(params)}`, {
    method: 'DELETE',
    credentials: 'include'
  }) : await fetch(SNOOZES_PATH, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      ...params,
      until: new Date(until).toISOString()
    })
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw Object.assign(new Error(body?.error ?? `Request failed with ${response.status}`), {
      status: response.status
    });
  }
}

/** The PR's snooze in the cache, if it has not ended. */
function getCachedUntil(client: QueryClient, pr: InboxPullRequest): number | null {
  const snoozes = client.getQueryData<InboxSnoozes>(inboxV2SnoozesQueryKey()) ?? NO_SNOOZES;
  const until = getSnoozedUntil(snoozes, pr);
  return until !== undefined && until > Date.now() ? until : null;
}
function setCachedUntil(client: QueryClient, pr: InboxPullRequest, until: number | null) {
  const key = getPrSnoozeKey(pr);
  client.setQueryData<InboxSnoozes>(inboxV2SnoozesQueryKey(), (snoozes = NO_SNOOZES) => until === null ? Object.fromEntries(Object.entries(snoozes).filter(([snoozeKey]) => snoozeKey !== key)) : {
    ...snoozes,
    [key]: until
  });
}

/** Writes one PR's snooze. The cache shows it at once and goes back if the write fails. */
function useWriteSnooze() {
  const $ = _c(2);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client) {
    t0 = {
      mutationKey: SNOOZE_WRITE_KEY,
      mutationFn: writeSnooze,
      onMutate: async t1 => {
        const {
          pr,
          until
        } = t1;
        await client.cancelQueries({
          queryKey: inboxV2SnoozesQueryKey()
        });
        const previous = getCachedUntil(client, pr);
        setCachedUntil(client, pr, until);
        return {
          previous
        };
      },
      onError: (error, t2, context) => {
        const {
          pr: pr_0,
          until: until_0
        } = t2;
        if (context) {
          setCachedUntil(client, pr_0, context.previous);
        }
        toast.error(`Could not ${until_0 === null ? "unsnooze" : "snooze"} #${pr_0.number}`, {
          description: error.message
        });
      },
      onSettled: () => {
        if (client.isMutating({
          mutationKey: SNOOZE_WRITE_KEY
        }) === 1) {
          client.invalidateQueries({
            queryKey: inboxV2SnoozesQueryKey()
          });
        }
      }
    };
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
const settle = () => undefined;

/**
 * Snoozes a PR until a time. Its toast's Undo puts back what the snooze replaced: no snooze, or
 * the earlier time, and calls `onUndo` first. The returned promise settles with the write and
 * never rejects.
 */
export function useSnoozePullRequest() {
  const $ = _c(4);
  const client = useQueryClient();
  const {
    mutate,
    mutateAsync
  } = useWriteSnooze();
  let t0;
  if ($[0] !== client || $[1] !== mutate || $[2] !== mutateAsync) {
    t0 = (pr, until, onUndo) => {
      const previous = getCachedUntil(client, pr);
      const written = mutateAsync({
        pr,
        until: until.getTime()
      }).then(settle, settle);
      toast.success(`Snoozed #${pr.number} until ${describeSnoozeTime(until, new Date())}`, {
        action: {
          label: "Undo",
          onClick: () => {
            onUndo?.();
            mutate({
              pr,
              until: previous,
              after: written
            });
          }
        }
      });
      return written;
    };
    $[0] = client;
    $[1] = mutate;
    $[2] = mutateAsync;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return useEventCallback(t0);
}

/** Ends a PR's snooze now. The returned promise settles with the write and never rejects. */
export function useUnsnoozePullRequest() {
  const $ = _c(2);
  const {
    mutateAsync
  } = useWriteSnooze();
  let t0;
  if ($[0] !== mutateAsync) {
    t0 = pr => {
      toast.success(`Unsnoozed #${pr.number}`);
      return mutateAsync({
        pr,
        until: null
      }).then(settle, settle);
    };
    $[0] = mutateAsync;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useEventCallback(t0);
}